import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { consumeCalibrationAttempt } from '#~/control/calibration/commands/attempt-id.js';
import {
  getLastCalibrationCancelMs,
  recordCalibrationCancel
} from '#~/control/calibration/commands/cancel-gate.js';
import type { CalibrationResult } from '#~/analytics/types/events.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { logger } from '#~/logger/index.js';
import { Status } from '@studio/models/control/calibration';
import type { Device } from '@studio/models/device';
import type { AnalyticsTrackFn } from './types.js';

/**
 * Deterministic map from proto calibration `Status` to the canonical
 * `CalibrationResult` (ENG-4026, CEO review: zero ambiguity for the
 * watchdog). RUNNING is a transition origin and NULL is cancel cleanup /
 * unknown — neither produces a terminal event, so both are absent from
 * this map and `calibrationResultForStatus` returns `undefined` for them.
 *
 * Per knowledge: state-result-mapping (ai.knowledge/ENG-4026).
 */
export const CALIBRATION_STATUS_RESULT: Partial<Record<Status, CalibrationResult>> = {
  [Status.CALIBRATION_STATE_SUCCESS]: 'success',
  [Status.CALIBRATION_STATE_OK]: 'success',
  [Status.CALIBRATION_STATE_OK_GENERIC]: 'generic_success',
  [Status.CALIBRATION_STATE_FAIL]: 'failed'
};

/**
 * Resolve the canonical calibration result for a proto `Status`.
 *
 * @param status - Proto calibration status enum value.
 * @returns The `CalibrationResult`, or `undefined` for RUNNING / NULL
 *   (no terminal event should be emitted for those states).
 */
export const calibrationResultForStatus = (status: Status): CalibrationResult | undefined =>
  CALIBRATION_STATUS_RESULT[status];

/**
 * TD3 cancel-gate window (ms). A user-initiated cancel within this window of
 * a terminal transition suppresses the `calibration_completed` emit (the
 * renderer cannot tell cancel-cleanup from real completion). ENG-4026.
 */
export const CALIBRATION_CANCEL_GATE_WINDOW_MS = 10_000;

/** Inputs to {@link decideCalibrationCompleted} — all the state needed to decide. */
export interface CalibrationTransitionInput {
  /** Status the device transitioned FROM. */
  previousStatus: Status | undefined;
  /** Status the device transitioned TO. */
  newStatus: Status;
  /** Calibration error string at the transition, if any. */
  // `| undefined` is required under tsconfig `exactOptionalPropertyTypes`
  // because the watchdog call site passes these explicitly (mirrors the
  // `studio_id?: string | undefined` convention in analytics/types/properties.ts).
  error?: string | undefined;
  /** Epoch ms of the most recent user cancel for this device (0/undefined = none). */
  lastCancelMs?: number | undefined;
  /** Current epoch ms (injected so tests can use fake timers deterministically). */
  nowMs: number;
}

/** Outcome of the calibration transition decision. */
export type CalibrationTransitionDecision =
  | {
      kind: 'emit';
      result: CalibrationResult;
      previousState: string;
      finalState: string;
      errorMessage?: string;
    }
  | { kind: 'suppressed' }
  | { kind: 'none' };

/**
 * Pure decision for whether a calibration status transition should emit a
 * `calibration_completed` event. This is the SINGLE source of truth for the
 * TD2 (first-transition-out-of-RUNNING) and TD3 (cancel-gate) rules — the
 * watchdog feeds it the per-device state and acts on the result, so there is
 * one tested code path rather than logic duplicated across component + tests.
 *
 * Per knowledge: td2-first-transition + td3-cancel-gate (ai.knowledge/ENG-4026).
 *
 * @returns
 *   - `none` — not a RUNNING->terminal transition (TD2), or the target state
 *     has no result (RUNNING / NULL). No event, no suppression log.
 *   - `suppressed` — a real RUNNING->terminal transition that the cancel gate
 *     (TD3) suppressed. The caller logs this so ITG can measure false-positives.
 *   - `emit` — emit `calibration_completed` with the carried fields.
 */
export const decideCalibrationCompleted = (
  input: CalibrationTransitionInput
): CalibrationTransitionDecision => {
  // TD2: only the FIRST transition OUT of RUNNING is terminal. The later 5s
  // SUCCESS->OK reclassification has previousStatus !== RUNNING, so it no-ops.
  if (input.previousStatus !== Status.CALIBRATION_STATE_RUNNING) {
    return { kind: 'none' };
  }

  const result = calibrationResultForStatus(input.newStatus);

  // RUNNING->RUNNING (no change) and RUNNING->NULL (cancel cleanup / unknown)
  // have no terminal result — nothing to emit.
  if (!result) {
    return { kind: 'none' };
  }

  // TD3: a cancel within the window suppresses. Direction-agnostic (OQ1, the
  // locked strict interpretation): this applies to RUNNING->FAIL too, not
  // just RUNNING->OK — any terminal transition shortly after a cancel is
  // treated as cancel fallout.
  if (input.nowMs - (input.lastCancelMs ?? 0) < CALIBRATION_CANCEL_GATE_WINDOW_MS) {
    return { kind: 'suppressed' };
  }

  return {
    kind: 'emit',
    result,
    previousState: Status[Status.CALIBRATION_STATE_RUNNING],
    finalState: Status[input.newStatus],
    // error_message only carries meaning on failure (state-result-mapping).
    ...(result === 'failed' && input.error ? { errorMessage: input.error } : {})
  };
};

/** Arguments for {@link createCalibrationLifecycleTracker}'s completed emit. */
export interface TrackCalibrationCompletedArgs {
  /** Device that completed (or terminally failed) calibration. */
  deviceId: Device['id'];
  /** Proto enum NAME string for the state we transitioned out of. */
  previousState: string;
  /** Proto enum NAME string for the terminal state we landed in. */
  finalState: string;
  /** Canonical terminal outcome. */
  result: CalibrationResult;
  /** Failure detail — only meaningful (and only included) when result==='failed'. */
  errorMessage?: string;
  /** Calibration progress (`0..100`) at the terminal transition, if known. */
  progress?: number;
  /** Wall-clock duration from RUNNING entry to terminal transition. */
  attemptDurationMs?: number;
  /** Client-synthesized attempt id (TD1) threading start -> completed. */
  attemptId?: string;
}

/**
 * Calibration lifecycle analytics tracker (ENG-4026).
 *
 * Mirrors the `createBridgeLifecycleTracker` factory pattern: takes the
 * `track` fn and returns terminal-event emitters. Device/studio/brand/model
 * context is resolved via the shared `resolveDeviceAnalyticsContext` — not
 * reinvented here (per think-in-systems: one primitive owns device context).
 */
export const createCalibrationLifecycleTracker = (track: AnalyticsTrackFn) => ({
  trackCalibrationCompleted: ({
    deviceId,
    previousState,
    finalState,
    result,
    errorMessage,
    progress,
    attemptDurationMs,
    attemptId
  }: TrackCalibrationCompletedArgs): void => {
    // Reuse the canonical device-context resolver for device/studio/brand/model.
    const deviceContext = resolveDeviceAnalyticsContext(deviceId) ?? { device_id: deviceId };

    const properties: EventProperties = {
      ...deviceContext,
      device_id: deviceContext.device_id ?? deviceId,
      previous_state: previousState,
      final_state: finalState,
      result,
      // error_message only carries meaning on failure (state-result-mapping).
      ...(result === 'failed' && errorMessage ? { error_message: errorMessage } : {}),
      ...(progress !== undefined ? { progress } : {}),
      ...(attemptDurationMs !== undefined ? { attempt_duration_ms: attemptDurationMs } : {}),
      ...(attemptId ? { attempt_id: attemptId } : {})
    };

    track(EVENT_NAMES.CALIBRATION_COMPLETED, EVENT_CATEGORIES.CALIBRATION, properties);

    // Dev breakpoint trail (DX review): leave a debug crumb keyed by the
    // join fields a future engineer needs to trace report-join failures.
    logger.debug('calibration_completed emitted', {
      attempt_id: attemptId,
      device_id: properties.device_id,
      result
    });
  }
});

/** Return type of {@link createCalibrationLifecycleTracker}. */
export type CalibrationLifecycleTracker = ReturnType<typeof createCalibrationLifecycleTracker>;

/**
 * Stateful per-device controller that owns the TD1/TD2/TD3 bookkeeping for
 * `calibration_completed` and emits via the tracker. Extracted from the
 * watchdog so the orchestration (per-device cancel gate, RUNNING-start
 * timing, attempt-id consume) is one tested primitive and the React
 * component is a thin adapter (think-in-systems, ENG-4026).
 *
 * All time reads go through the injected `now` thunk so tests drive it with
 * fake timers deterministically (defaults to `Date.now`).
 */
export const createCalibrationCompletionController = (
  track: AnalyticsTrackFn,
  now: () => number = Date.now
) => {
  const tracker = createCalibrationLifecycleTracker(track);

  // RUNNING-entry timestamps (attempt duration) stay watchdog-local. The TD3
  // cancel gate, however, lives in the shared `cancel-gate.ts` registry so the
  // badge-cancel path (stop.ts), which is outside this component, can arm it
  // too (ENG-4026: the primary cancel was previously ungated — false-positive
  // completions on cancel). Removal stays a one/two-file delete when the bridge
  // proto CALIBRATION_STATE_CANCELLED lands.
  const runningStartMs = new Map<Device['id'], number>();

  return {
    /**
     * Arm the TD3 cancel gate for a device via the shared registry. Called from
     * the watchdog's in-prompt cancels ("Use Generic Driver" / "Skip"); the
     * badge X cancel arms the same gate directly from stop.ts.
     */
    recordCancel(deviceId: Device['id']): void {
      recordCalibrationCancel(deviceId, now());
    },

    /** Note the moment a device first entered RUNNING (attempt start). */
    noteRunningStart(deviceId: Device['id']): void {
      runningStartMs.set(deviceId, now());
    },

    /**
     * Process one status transition. Delegates the emit/suppress/none
     * decision to {@link decideCalibrationCompleted}; on emit, enriches with
     * attempt id (TD1, consumed) and attempt duration; on suppress, logs so
     * ITG can measure the false-positive rate.
     */
    handleTransition(
      deviceId: Device['id'],
      previousStatus: Status | undefined,
      newStatus: Status,
      error?: string
    ): CalibrationTransitionDecision {
      const nowMs = now();
      const decision = decideCalibrationCompleted({
        previousStatus,
        newStatus,
        error,
        lastCancelMs: getLastCalibrationCancelMs(deviceId),
        nowMs
      });

      if (decision.kind === 'none') {
        return decision;
      }

      // Read start time then clear per-attempt state regardless of outcome,
      // so a stale start time / attempt id cannot bleed into a later attempt.
      const startedAt = runningStartMs.get(deviceId);
      runningStartMs.delete(deviceId);
      const attemptId = consumeCalibrationAttempt(deviceId);

      if (decision.kind === 'suppressed') {
        // TD3: every suppression logged (false-positive rate measurable in ITG).
        logger.debug('calibration_completed suppressed (recent cancel)', { deviceId });
        return decision;
      }

      tracker.trackCalibrationCompleted({
        deviceId,
        previousState: decision.previousState,
        finalState: decision.finalState,
        result: decision.result,
        ...(decision.errorMessage ? { errorMessage: decision.errorMessage } : {}),
        ...(attemptId ? { attemptId } : {}),
        ...(startedAt !== undefined ? { attemptDurationMs: Math.max(0, nowMs - startedAt) } : {})
      });

      return decision;
    }
  };
};

/** Return type of {@link createCalibrationCompletionController}. */
export type CalibrationCompletionController = ReturnType<
  typeof createCalibrationCompletionController
>;
